import type { CampaignAnnouncement } from './marketplaceHomeContent';
import type { ShoppingWalletAnnouncementSummary } from '@/presentation/stores/ShoppingWalletStore';

const DISMISSED_ANNOUNCEMENT_PREFIX = 'motito:campaign-strip:dismissed:';

function getSafeSessionStorage(): Pick<Storage, 'getItem'> | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function campaignAnnouncementDismissalKey(announcementId: string): string {
  return `${DISMISSED_ANNOUNCEMENT_PREFIX}${announcementId}`;
}

export function persistCampaignAnnouncementDismissal(announcementId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(campaignAnnouncementDismissalKey(announcementId), '1');
  } catch {
    // Browsers can block session storage; in-memory dismissal still works.
  }
}

export function isCampaignAnnouncementDismissed(
  announcementId?: string | null,
  storage: Pick<Storage, 'getItem'> | null = null
): boolean {
  const resolvedStorage = storage ?? getSafeSessionStorage();

  if (!announcementId || !resolvedStorage) {
    return false;
  }

  try {
    return resolvedStorage.getItem(campaignAnnouncementDismissalKey(announcementId)) === '1';
  } catch {
    return false;
  }
}

export function pickVisibleAnnouncement(
  candidates: Array<CampaignAnnouncement | null | undefined>,
  dismissedAnnouncementIds: ReadonlySet<string>
): CampaignAnnouncement | null {
  return (
    candidates.find(
      (candidate): candidate is CampaignAnnouncement => {
        if (!candidate) {
          return false;
        }
        return !dismissedAnnouncementIds.has(candidate.id);
      }
    ) ?? null
  );
}

export function shouldUseWalletAnnouncementAction(
  resolvedAnnouncement: CampaignAnnouncement | null,
  walletAnnouncement: CampaignAnnouncement | null
): boolean {
  return Boolean(
    resolvedAnnouncement &&
      walletAnnouncement &&
      resolvedAnnouncement.id === walletAnnouncement.id
  );
}

export function buildWalletAnnouncementId(
  summary: ShoppingWalletAnnouncementSummary,
  userId?: string | null
): string {
  const accountScope = userId || 'anonymous';
  const creditScope = summary.creditIds.length
    ? summary.creditIds.join(',')
    : `${summary.amount}:${summary.expiresAt || 'no-expiry'}`;

  return `shopping-wallet:${accountScope}:${creditScope}`;
}
